import React, { useEffect, useRef, useState } from 'react';
import { CaptureOverlay, useRotatingControls } from '../shared';
import { MoleculeSpinner } from '../../shared/MoleculeSpinner';
import { AUDIO } from '../../../../utils/audioFeedback';

interface PhotoViewfinderProps {
  taskName: string;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isReady: boolean;
  error: string | null;
  facingMode: 'user' | 'environment';
  lastPhoto: string | null;
  photoCount: number;
  zoomLevel: number;
  maxZoom: number;
  exposure: number;
  exposureRange: { min: number; max: number } | null;
  onCapture: () => void;
  onFlip: () => void;
  onStartCamera: () => void;
  onReview: () => void;
  onClose: () => void;
  onZoom: (zoom: number) => void;
  onExposure: (value: number) => void;
  onGalleryFiles: (files: FileList) => void;
}

export const PhotoViewfinder: React.FC<PhotoViewfinderProps> = ({
  taskName,
  videoRef,
  isReady,
  error,
  facingMode,
  lastPhoto,
  photoCount,
  zoomLevel,
  maxZoom,
  exposure,
  exposureRange,
  onCapture,
  onFlip,
  onStartCamera,
  onReview,
  onClose,
  onZoom,
  onExposure,
  onGalleryFiles,
}) => {
  const [showZoomIndicator, setShowZoomIndicator] = useState(false);
  const [showHint, setShowHint] = useState(true);
  const [flash, setFlash] = useState(false);
  const [showManualStart, setShowManualStart] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const { buttonStyle } = useRotatingControls();
  const pinchStartRef = useRef<{ distance: number; zoom: number } | null>(null);
  const zoomTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  // Defer camera start to next frame so video ref is attached after first paint
  useEffect(() => {
    const raf = requestAnimationFrame(() => onStartCamera());
    return () => cancelAnimationFrame(raf);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => setShowHint(false), 4000);
    return () => clearTimeout(t);
  }, []);

  // Show manual start button after 3s if camera hasn't started
  useEffect(() => {
    if (!isReady && !error) {
      const t = setTimeout(() => setShowManualStart(true), 3000);
      return () => clearTimeout(t);
    }
    setShowManualStart(false);
  }, [isReady, error]);

  const handleCapture = () => {
    AUDIO.shutter();
    setFlash(true);
    setTimeout(() => setFlash(false), 120);
    onCapture();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const distance = Math.hypot(
        e.touches[1].clientX - e.touches[0].clientX,
        e.touches[1].clientY - e.touches[0].clientY,
      );
      pinchStartRef.current = { distance, zoom: zoomLevel };
      setShowZoomIndicator(true);
      setShowHint(false);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length !== 2 || !pinchStartRef.current) return;
    const distance = Math.hypot(
      e.touches[1].clientX - e.touches[0].clientX,
      e.touches[1].clientY - e.touches[0].clientY,
    );
    const scale = distance / pinchStartRef.current.distance;
    onZoom(pinchStartRef.current.zoom * scale);
  };

  const handleTouchEnd = () => {
    pinchStartRef.current = null;
    if (zoomTimeoutRef.current) clearTimeout(zoomTimeoutRef.current);
    zoomTimeoutRef.current = setTimeout(() => setShowZoomIndicator(false), 1500);
  };

  const handleGalleryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onGalleryFiles(e.target.files);
      e.target.value = '';
    }
  };

  const zoomPct = maxZoom > 1 ? ((zoomLevel - 1) / (maxZoom - 1)) * 100 : 0;

  return (
    <CaptureOverlay taskName={taskName} onClose={onClose}>
      <div className="pc-body">
        <div
          className="pc-viewfinder"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            disablePictureInPicture
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
            }}
          />

          {/* Loading overlay */}
          {!isReady && !error && (
            <div className="pc-loading">
              <MoleculeSpinner />
              <span>Starting camera…</span>
              {showManualStart && (
                <button className="pc-manual-start" onClick={onStartCamera}>
                  Tap to start camera
                </button>
              )}
            </div>
          )}

          {/* Error overlay */}
          {error && (
            <div className="pc-error">
              <span>Camera not available</span>
              <button onClick={onStartCamera}>Retry</button>
            </div>
          )}

          {/* Flash overlay */}
          {flash && <div className="pc-flash" />}

          <div className="pc-vf-overlay">
            <div className="pc-vf-corner pc-vf-tl" />
            <div className="pc-vf-corner pc-vf-tr" />
            <div className="pc-vf-corner pc-vf-bl" />
            <div className="pc-vf-corner pc-vf-br" />
            {showGrid && (
              <div className="pc-grid">
                <div className="pc-grid-line pc-grid-h" style={{ top: '33.33%' }} />
                <div className="pc-grid-line pc-grid-h" style={{ top: '66.67%' }} />
                <div className="pc-grid-line pc-grid-v" style={{ left: '33.33%' }} />
                <div className="pc-grid-line pc-grid-v" style={{ left: '66.67%' }} />
              </div>
            )}
          </div>

          {/* Grid toggle */}
          <button
            className={`pc-grid-toggle${showGrid ? ' pc-grid-toggle--active' : ''}`}
            onClick={() => setShowGrid(g => !g)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <line x1="9" y1="3" x2="9" y2="21" />
              <line x1="15" y1="3" x2="15" y2="21" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="3" y1="15" x2="21" y2="15" />
            </svg>
          </button>

          <div className="pc-vf-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1">
              <circle cx="12" cy="12" r="5" />
              <line x1="12" y1="2" x2="12" y2="7" />
              <line x1="12" y1="17" x2="12" y2="22" />
              <line x1="2" y1="12" x2="7" y2="12" />
              <line x1="17" y1="12" x2="22" y2="12" />
            </svg>
          </div>

          {/* Zoom indicator */}
          {showZoomIndicator && maxZoom > 1 && (
            <div className="pc-zoom-indicator">
              <span className="pc-zoom-label">1&times;</span>
              <div className="pc-zoom-track">
                <div className="pc-zoom-fill" style={{ width: `${zoomPct}%` }} />
                <div className="pc-zoom-thumb" style={{ left: `${zoomPct}%` }} />
              </div>
              <span className="pc-zoom-value">{zoomLevel.toFixed(1)}&times;</span>
            </div>
          )}

          {/* Exposure slider */}
          {exposureRange && (
            <div className="pc-exposure-slider">
              <svg className="pc-exposure-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round">
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
              <input
                type="range"
                min={-2}
                max={2}
                step={0.1}
                value={exposure}
                onChange={(e) => onExposure(parseFloat(e.target.value))}
                className="pc-exposure-input"
              />
              <svg className="pc-exposure-icon pc-exposure-icon--dim" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round">
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            </div>
          )}

          {/* Hint */}
          {showHint && maxZoom > 1 && (
            <div className="pc-hint">Pinch to zoom</div>
          )}
        </div>

        {/* Hidden gallery input */}
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleGalleryChange}
          style={{ display: 'none' }}
        />

        {/* Controls */}
        <div className="pc-controls">
          {/* Left: thumbnail or gallery button */}
          {photoCount > 0 ? (
            <div className="pc-gallery-thumb pc-gallery-thumb--active" onClick={onReview}>
              {lastPhoto && <img src={lastPhoto} alt="Last capture" />}
              <span className="pc-badge">{photoCount}</span>
            </div>
          ) : (
            <button className="pc-gallery-btn" onClick={() => galleryInputRef.current?.click()}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" strokeLinecap="round" style={buttonStyle}>
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21,15 16,10 5,21" />
              </svg>
            </button>
          )}

          {/* Center: shutter */}
          <button className="pc-shutter" onClick={handleCapture} disabled={!isReady} style={buttonStyle} aria-label="Take photo" />

          {/* Right: flip camera */}
          <button className="pc-flip" onClick={onFlip} disabled={!isReady} aria-label="Flip camera">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" style={buttonStyle}>
              <polyline points="1,4 1,10 7,10" />
              <path d="M3.51 15a9 9 0 1 0 .49-4.95" />
            </svg>
          </button>
        </div>

        {/* Review bar — shown when photos exist */}
        {photoCount > 0 && (
          <div className="pc-review-bar">
            <button
              className="pc-add-gallery-btn"
              onClick={() => galleryInputRef.current?.click()}
            >
              + Gallery
            </button>
            <button className="pc-review-cta" onClick={onReview}>
              Review ({photoCount}) →
            </button>
          </div>
        )}
      </div>
    </CaptureOverlay>
  );
};
